import type {
  ApiErrorBody,
  CompleteUploadRequest,
  CompleteUploadResponse,
  CreateUploadRequest,
  CreateUploadResponse,
  ListPartsResponse,
  PublicConfig,
  RenderStatusResponse,
  SignPartsResponse,
} from '../../../shared/types';

/** Wraps an API failure with a message that is safe to show to end users. */
export class UserFacingError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
    this.name = 'UserFacingError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    });
  } catch (err) {
    throw new UserFacingError('network_error', 'Your internet connection was interrupted.');
  }

  if (response.status === 204) return undefined as T;

  if (!response.ok) {
    let body: ApiErrorBody | undefined;
    try {
      body = await response.json();
    } catch {
      // ignore parse failure, fall through to generic message
    }
    if (response.status === 429) {
      throw new UserFacingError('rate_limited', 'Too many requests right now. Please slow down and try again.');
    }
    throw new UserFacingError(body?.error ?? 'request_failed', body?.message ?? 'The request failed. Please try again.');
  }

  return response.json() as Promise<T>;
}

export function fetchPublicConfig(): Promise<PublicConfig> {
  return request<PublicConfig>('/api/config');
}

export function createUpload(payload: CreateUploadRequest): Promise<CreateUploadResponse> {
  return request<CreateUploadResponse>('/api/uploads/create', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function signParts(sessionId: string, partNumbers: number[]): Promise<SignPartsResponse> {
  return request<SignPartsResponse>(`/api/uploads/${sessionId}/sign-parts`, {
    method: 'POST',
    body: JSON.stringify({ partNumbers }),
  });
}

export function listParts(sessionId: string): Promise<ListPartsResponse> {
  return request<ListPartsResponse>(`/api/uploads/${sessionId}/parts`);
}

export function completeUpload(sessionId: string, payload: CompleteUploadRequest): Promise<CompleteUploadResponse> {
  return request<CompleteUploadResponse>(`/api/uploads/${sessionId}/complete`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function abortUpload(sessionId: string): Promise<void> {
  return request<void>(`/api/uploads/${sessionId}`, { method: 'DELETE' });
}

export function fetchRenderStatus(groupId: string): Promise<RenderStatusResponse> {
  return request<RenderStatusResponse>(`/api/render/${groupId}`);
}

export function retryRender(groupId: string): Promise<{ restarted: true }> {
  return request<{ restarted: true }>(`/api/render/${groupId}/retry`, { method: 'POST' });
}
